import type { ListTableRow, ListTableSort } from 'framework/ui/ui-builder';
import type { CollectionViewParamsConfig } from '../useControlUrlParams/types';
import type { CollectionGrouping, CollectionSection } from './collectionGroups';

export interface CollectionPageResponse<Row> {
  result: Row[];
  pageNumber: number;
  totalPages: number;
  totalElements: number;
}

export interface CollectionGroupedPageResponse<Row> extends CollectionPageResponse<Row> {
  sections: CollectionSection[];
}

export type CollectionSort = Record<string, ListTableSort['direction']>;

export interface FetchCollectionPageParams<Filters> {
  pageNumber: number;
  pageSize: number;
  filters: Filters;
  searchValue: string;
  sort?: CollectionSort;
  signal?: AbortSignal;
}

interface UseCollectionPagerBaseParams<Filters> {
  viewParamsConfig: CollectionViewParamsConfig<Filters>;
  pageSize: number;
  searchDebounceMs?: number;
  collectionKey?: string | number;
  enabled?: boolean;
}

export interface UseCollectionPagerParams<
  Row extends ListTableRow,
  Filters,
> extends UseCollectionPagerBaseParams<Filters> {
  fetchPage: (params: FetchCollectionPageParams<Filters>) => Promise<CollectionPageResponse<Row>>;
  fetchInitialPage?: (params: FetchCollectionPageParams<Filters>) => Promise<CollectionPageResponse<Row>>;
  grouping?: never;
}

export interface UseGroupedCollectionPagerParams<
  Row extends ListTableRow,
  Filters,
> extends UseCollectionPagerBaseParams<Filters> {
  fetchPage: (params: FetchCollectionPageParams<Filters>) => Promise<CollectionGroupedPageResponse<Row>>;
  fetchInitialPage?: (params: FetchCollectionPageParams<Filters>) => Promise<CollectionGroupedPageResponse<Row>>;
  grouping: CollectionGrouping<Row>;
}

export type UseAnyCollectionPagerParams<Row extends ListTableRow, Filters> =
  UseCollectionPagerParams<Row, Filters> | UseGroupedCollectionPagerParams<Row, Filters>;

export enum PageLoadDirection {
  REPLACE = 'replace',
  PREVIOUS = 'previous',
  NEXT = 'next',
}
